import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Mail, ShieldCheck, AlertCircle, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

export const MagicLinkAnfordern = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const senden = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post('/auth/magic-link/request', { email }); } catch { /* immer neutral */ }
    setSent(true); setBusy(false);
  };
  useEffect(() => { if (new URLSearchParams(window.location.search).get('magic')) document.querySelector('[data-testid="magic-link-email"]')?.focus(); }, []);
  if (sent) return <p className="text-sm text-emerald-800 bg-emerald-50 border-l-4 border-emerald-500 px-3 py-3" data-testid="magic-link-sent">{t('magic.sent')}</p>;
  return (
    <form onSubmit={senden} className="space-y-3" data-testid="magic-link-form">
      <p className="text-xs text-[#34536b]">{t('magic.hint')}</p>
      <div className="relative">
        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#34536b]/50" />
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@beispiel.de" data-testid="magic-link-email"
          className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 focus:border-[#b8893d] focus:outline-none text-sm rounded-sm" />
      </div>
      <button type="submit" disabled={busy || !email} data-testid="magic-link-submit"
        className="w-full inline-flex items-center justify-center gap-2 py-4 text-xs tracking-[0.15em] border-2 border-[#1B3A5C] text-[#1B3A5C] hover:bg-[#1B3A5C] hover:text-white transition-colors disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {t('magic.send')}
      </button>
    </form>
  );
};

export default function MagicLinkLogin() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithMagicLink, verify2fa } = useAuth();
  const [pending, setPending] = useState(null);
  const [otp, setOtp] = useState('');
  const [merken, setMerken] = useState(false);
  const [err, setErr] = useState('');
  const once = useRef(false);

  useEffect(() => {
    if (once.current) return;
    once.current = true;
    const token = params.get('token');
    window.history.replaceState({}, '', '/anmelden/link');
    loginWithMagicLink(token).then((r) => {
      if (r?.two_factor_required) setPending(r.pending_token);
      else navigate('/dashboard', { replace: true });
    }).catch((e) => setErr(e?.response?.status === 403 ? (e.response.data?.detail || t('magic.error')) : t('magic.error')));
  }, [params, loginWithMagicLink, navigate, t]);

  const pruefen = async (e) => {
    e.preventDefault();
    try { await verify2fa(pending, otp, merken); navigate('/dashboard', { replace: true }); } catch (x) { setErr(x?.response?.data?.detail || t('magic.error')); setPending(null); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-[160px] pb-20 bg-[#f5f8fa] min-h-[80vh]">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-white border border-gray-200 p-8 shadow-sm text-center" data-testid="magic-link-page">
            {err ? (<>
              <AlertCircle className="w-8 h-8 text-rose-600 mx-auto" />
              <p className="mt-3 text-sm text-[#1B3A5C]" data-testid="magic-link-error">{err}</p>
              <Link to="/login?magic=1" className="inline-block mt-5 text-xs tracking-[0.15em] text-[#b8893d] hover:text-[#1B3A5C]" data-testid="magic-link-neu">{t('magic.new_link')}</Link>
            </>) : pending ? (
              <form onSubmit={pruefen} className="space-y-4 text-left">
                <ShieldCheck className="w-8 h-8 text-[#b8893d] mx-auto" />
                <p className="text-sm text-[#1B3A5C] text-center">{t('magic.otp')}</p>
                <input inputMode="numeric" autoFocus value={otp} onChange={(e) => setOtp(e.target.value)} maxLength={6} data-testid="magic-link-otp"
                  className="w-full text-center tracking-[0.5em] font-mono text-lg py-3 border border-gray-200 focus:border-[#b8893d] focus:outline-none" />
                <label className="flex items-center gap-2 text-sm text-[#1B3A5C] cursor-pointer select-none">
                  <input type="checkbox" checked={merken} onChange={(e) => setMerken(e.target.checked)} className="w-4 h-4 accent-[#1B3A5C]" data-testid="magic-link-merken" />
                  Dieses Gerät für heute merken (bis Mitternacht kein Code)
                </label>
                <button type="submit" className="w-full py-3 text-xs tracking-[0.15em] bg-[#1B3A5C] text-white hover:bg-[#b8893d]" data-testid="magic-link-otp-submit">OK</button>
              </form>
            ) : (<><Loader2 className="w-8 h-8 animate-spin text-[#b8893d] mx-auto" /><p className="mt-3 text-sm text-[#34536b]">{t('magic.verifying')}</p></>)}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
